import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus, Trash2, ChevronUp, ChevronDown, Eye, Loader2 } from 'lucide-react';
import { apiFetch, ApiClientError } from '@/lib/api';
import { toast } from '@/stores/toast';
import { useAuthStore, hasTenantWidePermission } from '@/stores/auth';
import { Button } from '@/components/Button';
import { EmptyState } from '@/components/EmptyState';
import { ConfirmDialog } from '@/components/Modal';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useSkillLevelSimplified } from '@/hooks/useSkillLevelSimplified';
import { ConditionValueEditor } from './ConditionValueEditor';
import { ATTRIBUTE_TYPE_LABELS, MATCH_MODE_LABELS, OPERATOR_LABELS } from './labels';
import type {
  ConditionAttributeType, ConditionSetTreeNode, ConditionSetsResponse,
  ConditionUsagePoint, ConditionValue, PreviewResponse,
} from './types';

/** v0.4 章② (D-57): 条件集合の詳細モード (AND/OR) エディタ。
 *  簡易モード (`simple_editor_kind`) は消費機能 (必要人員=章④、可視性ルール/標準単価=章⑤) が
 *  まだ無いため章②では実装しない (§2-8 の指示、過剰実装しない)。
 *  設計: docs/design/v04-shift-labor-management-detail.md §2-8 */

type DraftCondition = { localId: string; attribute_type_id: string; operator: string; value: ConditionValue };
type Draft = { label: string; matchMode: 'all' | 'any'; conditions: DraftCondition[] };

let localIdCounter = 0;
const nextLocalId = () => `local-${++localIdCounter}`;

const toDraft = (set: ConditionSetTreeNode): Draft => ({
  label: set.label ?? '',
  matchMode: set.match_mode,
  conditions: set.conditions.map((cond) => ({
    localId: cond.condition_id, attribute_type_id: cond.attribute_type_id, operator: cond.operator, value: cond.value,
  })),
});

/** ⚠️ skill の operator='gte' は skill_id だけでなく min_level_sort_order も必須。
 *  未指定のまま保存すると evaluate.ts の evalSkill が operator==='gte' を無条件 true 扱いし、
 *  「習熟度◯以上」の条件が実質「スキル保有のみ」に静かに縮退する (QA/design-and-code 指摘)。 */
const isConditionValueEmpty = (value: ConditionValue, attributeTypeId: string, operator: string) => {
  if (attributeTypeId === 'skill') {
    if (!value.skill_id) return true;
    if (operator === 'gte' && value.min_level_sort_order === undefined) return true;
    return false;
  }
  if (attributeTypeId === 'employee_flag') return !value.flag;
  if (attributeTypeId === 'viewer_permission') return !value.action || !value.feature;
  if (attributeTypeId === 'target_node_relation') return !value.relation;
  return (value.ids?.length ?? 0) === 0 && (value.relations?.length ?? 0) === 0;
};

function ConditionSetNodeCard({
  set, depth, usagePoint, attributeTypesById, siblings, indexInSiblings, onChanged, canManage, readOnlyNoticeId,
}: {
  set: ConditionSetTreeNode;
  depth: number;
  usagePoint: ConditionUsagePoint;
  attributeTypesById: Map<string, ConditionAttributeType>;
  siblings: ConditionSetTreeNode[];
  indexInSiblings: number;
  onChanged: () => void;
  canManage: boolean;
  /** 読み取り専用の理由を説明する要素の id (aria-describedby で結ぶ)。 */
  readOnlyNoticeId: string;
}) {
  const [draft, setDraft] = useState<Draft>(() => toDraft(set));
  const [savedSnapshot, setSavedSnapshot] = useState<Draft>(() => toDraft(set));
  const [previewCount, setPreviewCount] = useState<number | null>(null);
  // 月次実績 I-5a (§7-1): target_node_relation を含む利用箇所 (work_record_approver) は店舗ごとに
  // 結果が変わるため、previewCount=null (未実行) と区別してこのフラグで「表示できない」旨を伝える。
  const [previewRequiresTargetContext, setPreviewRequiresTargetContext] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [reordering, setReordering] = useState(false);
  // v0.4 追加要件⑬ (D-395/D-398): 習熟度が実質未整備のテナントでは skill の演算子選択自体を出さず
  // has (保有のみ) 固定にする。
  const { isSimplified: skillLevelSimplified } = useSkillLevelSimplified();

  const allowedAttrTypes = useMemo(
    () => usagePoint.allowed_attribute_types
      .map((id) => attributeTypesById.get(id))
      .filter((t): t is ConditionAttributeType => !!t && t.is_implemented),
    [usagePoint, attributeTypesById],
  );

  const isDirty = JSON.stringify(draft) !== JSON.stringify(savedSnapshot);
  const hasEmptyCondition = draft.conditions.some((c) => isConditionValueEmpty(c.value, c.attribute_type_id, c.operator));
  const canSave = isDirty && !hasEmptyCondition;
  const canAddChild = depth < usagePoint.max_nesting_depth - 1;

  const saveMut = useMutation({
    mutationFn: () => apiFetch(`/condition-sets/${set.set_id}`, {
      method: 'PUT',
      body: JSON.stringify({
        match_mode: draft.matchMode,
        label: draft.label.trim() === '' ? null : draft.label.trim(),
        conditions: draft.conditions.map((c) => ({ attribute_type_id: c.attribute_type_id, operator: c.operator, value: c.value })),
      }),
    }),
    onSuccess: () => { setSavedSnapshot(draft); onChanged(); toast.success('条件セットを保存しました'); },
    onError: (e) => toast.error(e instanceof ApiClientError ? e.message : '保存に失敗しました'),
  });

  const deleteMut = useMutation({
    mutationFn: () => apiFetch(`/condition-sets/${set.set_id}`, { method: 'DELETE' }),
    onSuccess: () => { setConfirmDelete(false); onChanged(); toast.success('条件セットを削除しました'); },
    onError: (e) => { toast.error(e instanceof ApiClientError ? e.message : '削除に失敗しました (配下のセットが他の設定から参照されている可能性があります)'); setConfirmDelete(false); },
  });

  const addChildMut = useMutation({
    mutationFn: () => apiFetch('/condition-sets', {
      method: 'POST',
      body: JSON.stringify({ usage_point_id: usagePoint.usage_point_id, parent_set_id: set.set_id, match_mode: 'all', conditions: [] }),
    }),
    onSuccess: () => onChanged(),
    onError: (e) => toast.error(e instanceof ApiClientError ? e.message : '子セットの追加に失敗しました'),
  });

  const previewMut = useMutation({
    mutationFn: () => apiFetch<PreviewResponse>(`/condition-sets/${set.set_id}/preview`, { method: 'POST', body: JSON.stringify({}) }),
    onSuccess: (r) => { setPreviewCount(r.matched_count); setPreviewRequiresTargetContext(r.requires_target_context); },
    onError: (e) => toast.error(e instanceof ApiClientError ? e.message : 'プレビューに失敗しました'),
  });

  const move = async (direction: -1 | 1) => {
    const targetIndex = indexInSiblings + direction;
    if (targetIndex < 0 || targetIndex >= siblings.length) return;
    const target = siblings[targetIndex]!;
    setReordering(true);
    try {
      await Promise.all([
        apiFetch(`/condition-sets/${set.set_id}`, { method: 'PUT', body: JSON.stringify({ sort_order: target.sort_order }) }),
        apiFetch(`/condition-sets/${target.set_id}`, { method: 'PUT', body: JSON.stringify({ sort_order: set.sort_order }) }),
      ]);
      onChanged();
    } catch (e) {
      toast.error(e instanceof ApiClientError ? e.message : '並び替えに失敗しました');
    } finally {
      setReordering(false);
    }
  };

  const updateCondition = (index: number, patch: Partial<DraftCondition>) => {
    setDraft((prev) => ({
      ...prev,
      conditions: prev.conditions.map((c, i) => (i === index ? { ...c, ...patch } : c)),
    }));
  };
  const removeCondition = (index: number) => {
    setDraft((prev) => ({ ...prev, conditions: prev.conditions.filter((_, i) => i !== index) }));
  };
  const addCondition = () => {
    const first = allowedAttrTypes[0];
    if (!first) return;
    setDraft((prev) => ({
      ...prev,
      conditions: [...prev.conditions, { localId: nextLocalId(), attribute_type_id: first.attribute_type_id, operator: first.allowed_operators[0] ?? 'in', value: {} }],
    }));
  };

  const sortedChildren = useMemo(() => [...set.children].sort((a, b) => a.sort_order - b.sort_order), [set.children]);

  return (
    <div className="space-y-3">
      <div className="border border-surface-border rounded-lg bg-white p-3 sm:p-4 space-y-3">
        {/* ヘッダー: ラベル・AND/OR・並び替え・プレビュー・削除 */}
        <div className="flex flex-col sm:flex-row sm:items-center gap-2">
          <input
            type="text"
            value={draft.label}
            onChange={(e) => setDraft((prev) => ({ ...prev, label: e.target.value }))}
            placeholder="ラベル (任意、例: 自由が丘店の必要人員パターン)"
            maxLength={200}
            disabled={!canManage}
            aria-describedby={canManage ? undefined : readOnlyNoticeId}
            className="flex-1 min-w-0 rounded border border-surface-border px-2.5 py-2 text-sm focus-ring disabled:opacity-45 disabled:cursor-not-allowed"
            aria-label="条件セットのラベル"
          />
          <div className="flex items-center gap-1.5 flex-wrap">
            <div className="flex rounded-full border border-surface-border overflow-hidden" role="group" aria-label="条件の組み合わせ方">
              {(['all', 'any'] as const).map((mode) => (
                <button
                  key={mode}
                  type="button"
                  aria-pressed={draft.matchMode === mode}
                  disabled={!canManage}
                  aria-describedby={canManage ? undefined : readOnlyNoticeId}
                  onClick={() => setDraft((prev) => ({ ...prev, matchMode: mode }))}
                  className={`px-3 min-h-[44px] sm:min-h-[32px] text-xs font-medium disabled:opacity-45 disabled:cursor-not-allowed ${
                    draft.matchMode === mode ? 'bg-brand-red text-white' : 'bg-white text-text-body hover:bg-surface-sunken'
                  }`}
                >
                  {MATCH_MODE_LABELS[mode]}
                </button>
              ))}
            </div>
            {usagePoint.match_semantics === 'first_match' && (
              <div className="flex items-center gap-0.5">
                <button type="button" aria-label="この条件セットを上へ" disabled={!canManage || indexInSiblings === 0 || reordering}
                  onClick={() => move(-1)}
                  aria-describedby={canManage ? undefined : readOnlyNoticeId}
                  className="w-11 h-11 sm:w-8 sm:h-8 inline-flex items-center justify-center rounded text-text-sub hover:text-text-body disabled:opacity-45 disabled:cursor-not-allowed focus-ring">
                  <ChevronUp size={14} />
                </button>
                <button type="button" aria-label="この条件セットを下へ" disabled={!canManage || indexInSiblings === siblings.length - 1 || reordering}
                  onClick={() => move(1)}
                  aria-describedby={canManage ? undefined : readOnlyNoticeId}
                  className="w-11 h-11 sm:w-8 sm:h-8 inline-flex items-center justify-center rounded text-text-sub hover:text-text-body disabled:opacity-45 disabled:cursor-not-allowed focus-ring">
                  <ChevronDown size={14} />
                </button>
              </div>
            )}
            <Button variant="ghost" size="sm" leftIcon={previewMut.isPending ? <Loader2 size={14} className="animate-spin" /> : <Eye size={14} />}
              onClick={() => previewMut.mutate()} disabled={previewMut.isPending}>
              該当件数
            </Button>
            {canManage && (
              <button type="button" aria-label="この条件セットを削除" onClick={() => setConfirmDelete(true)}
                className="w-11 h-11 sm:w-8 sm:h-8 inline-flex items-center justify-center rounded text-text-sub hover:text-danger-text focus-ring">
                <Trash2 size={14} />
              </button>
            )}
          </div>
        </div>

        {previewRequiresTargetContext && (
          <p className="text-xs text-text-sub" aria-live="polite">この条件は基準店舗ごとに結果が変わるため、対象人数は表示できません</p>
        )}
        {!previewRequiresTargetContext && previewCount !== null && (
          <p className="text-xs text-text-sub" aria-live="polite">現在 <span className="font-semibold text-text-body tabular-nums">{previewCount}</span> 名が該当します</p>
        )}

        {/* 条件一覧 */}
        <div className="space-y-2">
          {draft.conditions.map((cond, i) => {
            const attrType = attributeTypesById.get(cond.attribute_type_id);
            return (
              <div key={cond.localId} className="flex flex-col sm:flex-row gap-2 sm:items-start border border-surface-line rounded p-2">
                <div className="flex gap-2 sm:w-72 shrink-0">
                  <div className="flex-1 min-w-0">
                    <Select
                      value={cond.attribute_type_id}
                      onValueChange={(v) => {
                        const nextType = attributeTypesById.get(v);
                        updateCondition(i, { attribute_type_id: v, operator: nextType?.allowed_operators[0] ?? 'in', value: {} });
                      }}
                      disabled={!canManage}
                    >
                      <SelectTrigger aria-label="属性型"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {allowedAttrTypes.map((t) => (
                          <SelectItem key={t.attribute_type_id} value={t.attribute_type_id}>
                            {ATTRIBUTE_TYPE_LABELS[t.attribute_type_id] ?? t.attribute_type_id}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="w-32 shrink-0">
                    {cond.attribute_type_id === 'skill' && skillLevelSimplified ? (
                      // v0.4 追加要件⑬ (D-395/D-398): 習熟度1段階のみのテナントでは演算子選択自体を出さない
                      <p className="min-h-[44px] sm:min-h-[32px] flex items-center text-sm text-text-sub">
                        <span className="sr-only">演算子: </span>{OPERATOR_LABELS.has}
                      </p>
                    ) : (
                    <Select
                      value={cond.operator}
                      onValueChange={(v) => {
                        // position_level は gte/lte (sort_order による数値比較) と in/not_in/eq (ID一致) で
                        // value_json の形が異なるため、その境界を跨ぐ切替では値をリセットする
                        // (跨がないと ids はあるが sort_order が無い壊れた状態のまま保存できてしまう)。
                        const crossesOrderBoundary = cond.attribute_type_id === 'position_level'
                          && (['gte', 'lte'].includes(v) !== ['gte', 'lte'].includes(cond.operator));
                        // skill: gte から離れる際は min_level_sort_order を捨てる (評価では無視される値だが、
                        // 「eq はskill_idのみ」というvalue_jsonの形の期待と食い違ったまま残さない、QA指摘)。
                        const leavesSkillGte = cond.attribute_type_id === 'skill' && cond.operator === 'gte' && v !== 'gte';
                        const shouldTruncate = v === 'eq' && (cond.value.ids?.length ?? cond.value.relations?.length ?? 0) > 1;
                        updateCondition(i, {
                          operator: v,
                          value: crossesOrderBoundary
                            ? {}
                            : leavesSkillGte
                              ? { skill_id: cond.value.skill_id }
                              : shouldTruncate
                                ? (cond.value.ids ? { ids: [cond.value.ids[0]!] } : { relations: [cond.value.relations![0]!] })
                                : cond.value,
                        });
                      }}
                      disabled={!canManage}
                    >
                      <SelectTrigger aria-label="演算子"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {(attrType?.allowed_operators ?? []).map((op) => (
                          <SelectItem key={op} value={op}>{OPERATOR_LABELS[op] ?? op}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    )}
                  </div>
                </div>
                <div className="flex-1 min-w-0">
                  {attrType ? (
                    <ConditionValueEditor attributeType={attrType} operator={cond.operator} value={cond.value}
                      onChange={(v) => updateCondition(i, { value: v })} disabled={!canManage}
                      usagePointFeature={usagePoint.feature} />
                  ) : (
                    <p className="text-xs text-danger-text">不明な属性型です</p>
                  )}
                  {isConditionValueEmpty(cond.value, cond.attribute_type_id, cond.operator) && (
                    <p className="text-xs text-danger-text mt-1">値を1件以上選択してください</p>
                  )}
                </div>
                {canManage && (
                  <button type="button" aria-label="この条件を削除" onClick={() => removeCondition(i)}
                    className="w-11 h-11 shrink-0 inline-flex items-center justify-center rounded text-text-sub hover:text-danger-text focus-ring self-start">
                    <Trash2 size={14} />
                  </button>
                )}
              </div>
            );
          })}
          {canManage && (
            <Button variant="ghost" size="sm" leftIcon={<Plus size={14} />} onClick={addCondition} disabled={allowedAttrTypes.length === 0}>
              条件を追加
            </Button>
          )}
        </div>

        <div className="flex items-center justify-between flex-wrap gap-2 pt-1">
          {canAddChild ? (
            canManage && (
              <Button variant="secondary" size="sm" leftIcon={<Plus size={14} />} onClick={() => addChildMut.mutate()} disabled={addChildMut.isPending}>
                子セットを追加
              </Button>
            )
          ) : (
            // ⚠️ QA指摘: ネスト上限の注記は権限に関わらず出す (権限ゲートの内側に置くと
            // 閲覧者には「なぜ子セットが増やせないのか」が一切分からなくなる)。
            <p className="text-xs text-text-sub">これ以上ネストできません (上限{usagePoint.max_nesting_depth}階層)</p>
          )}
          {canManage && (
            <div className="flex items-center gap-2">
              {isDirty && (
                <Button variant="ghost" size="sm" onClick={() => setDraft(savedSnapshot)}>破棄</Button>
              )}
              <Button variant="primary" size="sm" disabled={!canSave || saveMut.isPending} loading={saveMut.isPending} onClick={() => saveMut.mutate()}>
                保存
              </Button>
            </div>
          )}
        </div>
      </div>

      {sortedChildren.length > 0 && (
        // §2-8 明記: 「モバイルではネストを2階層までのカード積みにする — インデント表現は375pxで潰れる」。
        // sm 未満はインデント/罫線を外しフルwidthのカード積みにし、sm 以上でのみインデント表現に切り替える。
        <div className="ml-0 sm:ml-5 pl-0 sm:pl-3 border-0 sm:border-l-2 border-surface-border space-y-3">
          {sortedChildren.map((child, idx, arr) => (
            <ConditionSetNodeCard key={child.set_id} set={child} depth={depth + 1} usagePoint={usagePoint}
              attributeTypesById={attributeTypesById} siblings={arr} indexInSiblings={idx} onChanged={onChanged}
              canManage={canManage} readOnlyNoticeId={readOnlyNoticeId} />
          ))}
        </div>
      )}

      <ConfirmDialog
        open={confirmDelete}
        title="条件セットを削除"
        message="この条件セットを削除します。配下の子セットも同時に削除されます。この操作は取り消せません。"
        confirmLabel="削除する"
        destructive
        loading={deleteMut.isPending}
        onClose={() => setConfirmDelete(false)}
        onConfirm={() => deleteMut.mutate()}
      />
    </div>
  );
}

export function ConditionSetEditor({ usagePointId, usagePoint, attributeTypesById }: {
  usagePointId: string;
  usagePoint: ConditionUsagePoint;
  attributeTypesById: Map<string, ConditionAttributeType>;
}) {
  const qc = useQueryClient();
  // ⚠️ TD-22: 条件集合の書込み (POST/PUT/DELETE /condition-sets) は利用箇所に関わらず
  // routes/condition-sets.ts の requireTenantWideManage を通り、tenant_config:manage の
  // 全店舗スコープを要求する。呼び出し元ごとに述語が変わらないためここで解決する
  // (プレビュー POST /condition-sets/:id/preview は view 権限のみのため常に有効のまま)。
  const user = useAuthStore((s) => s.user);
  const canManage = hasTenantWidePermission(user?.effective_permissions, 'tenant_config', 'manage');
  // ui-guidelines.md §「削除ボタンdisabled」: disabled の理由は title ではなく aria-describedby で結ぶ。
  const readOnlyNoticeId = `condition-sets-readonly-${usagePointId}`;
  const { data: setsData, isLoading } = useQuery<ConditionSetsResponse>({
    queryKey: ['condition-sets', usagePointId],
    queryFn: () => apiFetch<ConditionSetsResponse>(`/condition-sets?usage_point_id=${encodeURIComponent(usagePointId)}`),
  });

  const onChanged = () => qc.invalidateQueries({ queryKey: ['condition-sets', usagePointId] });

  const addRootMut = useMutation({
    mutationFn: () => apiFetch('/condition-sets', {
      method: 'POST',
      body: JSON.stringify({ usage_point_id: usagePointId, match_mode: 'all', conditions: [] }),
    }),
    onSuccess: () => { onChanged(); toast.success('条件セットを追加しました'); },
    onError: (e) => toast.error(e instanceof ApiClientError ? e.message : '追加に失敗しました'),
  });

  const roots = useMemo(() => [...(setsData?.sets ?? [])].sort((a, b) => a.sort_order - b.sort_order), [setsData]);

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <p className="text-xs text-text-sub">
          {usagePoint.match_semantics === 'first_match'
            ? '上から順に評価し、最初に一致した条件セットが採用されます。'
            : '登録したすべての条件セットが独立に評価されます (優先順位はありません)。'}
          {!canManage && (
            <>
              {' '}
              <span id={readOnlyNoticeId}>
                閲覧のみ可能です (編集にはテナント全体の管理権限が必要です)。該当件数の確認は閲覧権限だけでも行えます。
              </span>
            </>
          )}
        </p>
        {canManage && (
          <Button variant="secondary" size="sm" leftIcon={<Plus size={14} />} onClick={() => addRootMut.mutate()} disabled={addRootMut.isPending}>
            条件セットを追加
          </Button>
        )}
      </div>
      {isLoading ? (
        <div className="text-sm text-text-sub">読み込み中…</div>
      ) : roots.length === 0 ? (
        <EmptyState
          message="条件セットがまだありません"
          description={canManage
            ? '「条件セットを追加」から作成してください。未設定の間、この利用箇所では条件による絞り込みは行われません。'
            : '未設定の間、この利用箇所では条件による絞り込みは行われません。編集にはテナント全体の管理権限が必要です。'}
        />
      ) : (
        <div className="space-y-3">
          {roots.map((set, idx) => (
            <ConditionSetNodeCard key={set.set_id} set={set} depth={0} usagePoint={usagePoint}
              attributeTypesById={attributeTypesById} siblings={roots} indexInSiblings={idx} onChanged={onChanged}
              canManage={canManage} readOnlyNoticeId={readOnlyNoticeId} />
          ))}
        </div>
      )}
    </div>
  );
}
